import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { studentAPI } from '../../services/api';

function ProjectTest({ projectId, projectName, onBack, onComplete }) {
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const fetchQuestions = useCallback(async () => {
        try {
            const response = await studentAPI.getTest(projectId);
            setQuestions(response.data.questions);
            setAnswers(new Array(response.data.questions.length).fill(''));
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load test');
            onBack();
        } finally {
            setLoading(false);
        }
    }, [projectId, onBack]);

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);

    const handleAnswerChange = (index, value) => {
        const newAnswers = [...answers];
        newAnswers[index] = value;
        setAnswers(newAnswers);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check if all questions are answered
        const unanswered = answers.some((ans) => !ans.trim());
        if (unanswered) {
            toast.warning('Please answer all questions');
            return;
        }

        setSubmitting(true);

        try {
            const formattedAnswers = answers.map((answer, index) => ({
                questionIndex: index,
                answer: answer.trim(),
            }));

            const response = await studentAPI.submitTest(projectId, formattedAnswers);
            setResult(response.data);

            if (response.data.passed) {
                toast.success(`Congratulations! You passed with ${response.data.score}%!`);
            } else {
                toast.error(`You scored ${response.data.score}%. You need 80% to pass.`);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to submit test');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-screen">
                <div className="spinner"></div>
                <p>Loading test questions...</p>
            </div>
        );
    }

    if (result) {
        return (
            <div className="test-result-page">
                <div className="result-card">
                    <div className={`result-icon ${result.passed ? 'success' : 'failure'}`}>
                        {result.passed ? '🎉' : '📚'}
                    </div>

                    <h2>{result.passed ? 'Congratulations!' : 'Keep Learning!'}</h2>

                    <div className="score-display">
                        <div className="score-circle">
                            <span className="score-value">{result.score}%</span>
                        </div>
                        <p className="score-details">
                            You got {result.correctAnswers} out of {result.totalQuestions} questions correct
                        </p>
                    </div>

                    {result.passed ? (
                        <div className="success-message">
                            <p>You've passed the test! The admin will upload your certificate soon.</p>
                            <p>You'll be able to download it from the project page.</p>
                        </div>
                    ) : (
                        <div className="failure-message">
                            <p>You need 80% or higher to pass.</p>
                            <p>Don't give up! Review the project materials and try again.</p>
                        </div>
                    )}

                    <div className="result-actions">
                        <button onClick={onComplete} className="btn btn-primary">
                            Back to Projects
                        </button>
                        {!result.passed && (
                            <button onClick={() => window.location.reload()} className="btn btn-secondary">
                                Retake Test
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="project-test">
            <button onClick={onBack} className="btn btn-back">
                ← Back
            </button>

            <div className="test-header">
                <h2>Project Test: {projectName}</h2>
                <p className="test-instructions">
                    Answer all 10 questions below. You need to score 80% or higher to pass.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="test-form">
                {questions.map((q, index) => (
                    <div key={index} className="question-card">
                        <div className="question-number">Question {index + 1} of {questions.length}</div>
                        <h3 className="question-text">{q.question}</h3>
                        <textarea
                            value={answers[index]}
                            onChange={(e) => handleAnswerChange(index, e.target.value)}
                            placeholder="Type your answer here..."
                            rows="4"
                            required
                        />
                    </div>
                ))}

                <div className="test-submit">
                    <button type="submit" className="btn btn-primary btn-large" disabled={submitting}>
                        {submitting ? 'Submitting...' : 'Submit Test'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default ProjectTest;
